<template>

    <div class="userform__page">

        <div v-show="step === 1" class="userform__bloc">

            <h1 class="cf-title-3 userform__title">{{ $t('user_form_title') }}</h1>

            <span class="userform__subtitle">
                <p>{{ $t('user_form_subtitle_created') }}</p>
                <p>{{ $t('user_form_subtitle_last_thing') }}</p>
            </span>

            <input2 ref="inputFirstname"
                    :default="user.firstname || ''"
                    :error="computedErrorFirstname"
                    :errorText="computedErrorTextFirstname"
                    :name="'fname'"
                    :placeholder="$t('user_form_firstname')"
                    :valid="computedValidFirstname"
                    class="userform__input"
                    @blur="blurInputFirstname"
                    @focus="focusInputFirstname"
                    @update="updateFirstname"
            ></input2>

            <input2 ref="inputLastname"
                    :default="user.lastname || ''"
                    :error="computedErrorLastname"
                    :errorText="computedErrorTextLastname"
                    :name="'lname'"
                    :placeholder="$t('user_form_name')"
                    class="userform__input"
                    @blur="blurInputLastname"
                    @focus="focusInputLastname"
                    @update="updateLastname"
            ></input2>

            <input2 ref="inputBirthyear"
                    :default="defaultBirthyear"
                    :error="computedErrorBirthYear"
                    :errorText="computedErrorTextBirthYear"
                    :placeholder="$t('user_form_birthyear')"
                    :valid="computedValidBirthYear"
                    class="userform__input"
                    @blur="blurInputBirthyear"
                    @focus="focusInputBirthyear"
                    @pressEnter="btnNext"
                    @update="updateBirthYear"
            ></input2>

            <button :class="{ 'userform__cta-active': isValidForm }" class="userform__cta" @click="nextStep">

                <lottie-loader v-if="requesting" :size="24"></lottie-loader>
                <span v-else>{{ $t('user_form_cta') }}</span>

            </button>

        </div>

        <div v-show="step === 2" class="userform__bloc">

            <h1 class="cf-title-3 userform__title">{{ $t('referral_title') }}</h1>

            <p class="userform__subtitle">{{ $t('referral_description') }}</p>

            <input2 ref="inputCode"
                    :error="isErrorCode"
                    :errorText="codeErrorText"
                    :placeholder="$t('referral_place_holder')"
                    @blur="blurInputCode"
                    @focus="focusInputCode"
                    @update="updateCode"></input2>

            <button :class="{ 'userform__cta-active': code && code.length }" class="userform__cta" @click="sendCode">
                <lottie-loader v-if="requesting" :size="24"></lottie-loader>
                <span v-else>{{ $t('referral_cta_validate') }}</span>
            </button>

            <button class="userform__cta-pass" @click="goFetch">{{ $t('referral_cta_skip') }}</button>

        </div>

        <div v-if="step === 3" class="userform__bloc">

            <img class="userform__success-icon" src="/img/inputs/input-success.svg">

            <h1 class="cf-title-3 userform__title">{{ $t('referral_success_title') }}</h1>

            <p class="userform__subtitle">{{ $t('referral_success_description') }}</p>

            <button class="userform__cta userform__cta-active" @click="goFetch">{{ $t('referral_success_cta_carry_on') }}</button>

        </div>

    </div>

</template>

<script>

import Vuex from 'vuex'
import Api from '@/api'

import Storage from '@/helpers/storage'

import Tag from '@/modules/tag'
import UserAgent from '@/modules/userAgent'
import UserFlow from '@/modules/userFlow'

import Input2 from '@/components/inputs/Input2'
import LottieLoader from '@/components/lottie/LottieLoader'

export default {

    name: 'user-form',

    components: {Input2, LottieLoader},

    computed: {

        ...Vuex.mapGetters(['user']),

        computedErrorFirstname() {
            return !!(this.firstname.length === 1 || this.errorFirstnameArray && this.errorFirstnameArray.length)
        },

        computedErrorTextFirstname() {
            return this.errorFirstnameArray ? this.$tc('user_form_firstname_invalid', this.errorFirstnameArray.length, {'invalid_characters': this.beautifull(this.errorFirstnameArray)}) : this.$t('user_form_firstname_error')
        },

        computedErrorLastname() {
            return !!(this.lastname.length === 1 || this.errorLastnameArray && this.errorLastnameArray.length)
        },

        computedErrorTextLastname() {
            return this.errorLastnameArray ? this.$tc('user_form_name_invalid', this.errorLastnameArray.length, {'invalid_characters': this.beautifull(this.errorLastnameArray)}) : this.$t('user_form_name_error')
        },

        computedErrorBirthYear() {
            return !(this.birthYear.length === 0 || this.birthYear.length === 4)
        },

        computedErrorTextBirthYear() {
            return this.errorBirthYear ? this.$t(`user_form_error_birthyear_old`) : this.$t('user_form_birthyear_error')
        },

        computedValidFirstname() {
            return this.firstname.length > 1 && !this.computedErrorFirstname
        },

        computedValidBirthYear() {
            const year = parseInt(this.birthYear, 10)
            const old = new Date().getFullYear() - year
            return !!(old && old > 12 && old < 101)
        },

        isValidForm() {
            return this.computedValidFirstname && this.computedValidBirthYear
        },

        computedFocused() {

            return this.inputFirstnameFocused || this.inputLastnameFocused || this.inputBirthyearFocused || this.inputCodeFocused

        },

        defaultBirthyear() {

            return this.user.birthYear > 0 ? this.user.birthYear.toString() : ''
        }

    },

    watch: {

        computedFocused: function (bool) {

            bool ? this.eventFocus() : this.showBackgroundTimer()

        }
    },

    data() {
        return {
            firstname: '',
            lastname: '',
            birthYear: '',
            errorFirstnameArray: null,
            errorLastnameArray: null,
            errorBirthYear: null,
            showBackground: true,
            showBackgroundTemp: true,
            step: 1,
            code: '',
            requesting: false,
            isErrorCode: false,
            isValidCode: false,
            codeErrorText: '',
            inputFirstnameFocused: false,
            inputLastnameFocused: false,
            inputBirthyearFocused: false,
            inputCodeFocused: false,
        }
    },

    created() {
        UserFlow.setPage(this, 'user_form')
    },

    methods: {

        ...Vuex.mapActions([
            'setUser'
        ]),

        updateFirstname(value) {
            this.firstname = value
            this.errorFirstnameArray = null
        },

        updateLastname(value) {
            this.lastname = value
            this.errorLastnameArray = null
        },

        updateBirthYear(value) {
            this.birthYear = value
            this.errorBirthYear = null
        },

        btnNext() {
            if (this.isValidForm) {
                this.nextStep()
            }
        },

        updateCode(code) {

            this.isErrorCode = false
            this.code = code

        },

        nextStep() {

            Tag.clickAmplitude(this, 'next', this.user.accounts[0] ? this.user.accounts[0].provider : '')

            if (this.isValidForm) {

                this.requesting = true

                Api.user.updateUser({
                    firstname: this.firstname,
                    lastname: this.lastname,
                    birthYear: this.birthYear,
                    device: UserAgent.getDevice(),
                })
                    .then(user => {
                        this.requesting = false
                        this.step = 2
                        const code = Storage.getItem('partnerCode')
                        this.$refs.inputCode.value = code
                        this.setUser(user)

                        UserFlow.setPage(this, 'referral')
                    })
                    .catch(error => {
                        this.requesting = false
                        if (error && error.message === 'invalid_arguments') {
                            this.parseError(error)
                        }
                    })
            }
        },

        goFetch() {

            Tag.clickAmplitude(this, 'skip_screen')
            Storage.removeItem('partnerCode')
            this.$router.push({path: '/fetch'})

        },

        sendCode() {

            if (this.code.length) {

                this.requesting = true

                Api.sponsorship.addCode(this.code)
                    .then(() => {
                        this.requesting = false
                        this.step = 3
                        Tag.clickAmplitude(this, 'code_validation')
                        Tag.pageViewGA(this, 'referral_success')

                    })
                    .catch(error => {
                        this.isValidCode = false
                        this.isErrorCode = true
                        this.requesting = false


                        switch (error.message) {
                            case 'unknown_code':
                                this.codeErrorText = this.$t('referral_error')
                                break
                            case 'cycling_referral':
                            case 'already_referred':
                            case 'self_add':
                            // should not happen
                            default:
                                this.codeErrorText = this.$t('cleaning_list_technical_error')
                                break
                        }

                    })
            }

        },

        parseError(error) {

            const obj = {}
            error.data.forEach(data => {
                obj[data.name] = data.error
            })

            this.errorFirstnameArray = obj.firstname ? obj.firstname[0].characters : null
            this.errorLastnameArray = obj.lastname ? obj.lastname[0].characters : null
            this.errorBirthYear = obj.birthYear ? obj.birthYear[0].error : null

        },

        beautifull(array) {
            return array.map(char => `"${char}"`)
                .toString()
                .replace(',', ', ')
        },

        eventFocus() {
            this.showBackground = false
            this.showBackgroundTemp = false
        },

        showBackgroundTimer() {
            this.showBackground = true
            setTimeout(() => {
                if (this.showBackground) {
                    this.showBackgroundTemp = true
                }
            }, 100)
        },

        focusInputFirstname() {
            this.inputFirstnameFocused = true
        },
        blurInputFirstname() {
            this.inputFirstnameFocused = false
        },
        focusInputLastname() {
            this.inputLastnameFocused = true
        },
        blurInputLastname() {
            this.inputLastnameFocused = false
        },
        focusInputBirthyear() {
            this.inputBirthyearFocused = true
        },
        blurInputBirthyear() {
            this.inputBirthyearFocused = false
        },
        focusInputCode() {
            this.inputCodeFocused = true
        },
        blurInputCode() {
            this.inputCodeFocused = false
        },
    }
}
</script>

<style lang="scss" scoped>


.userform {

    &__page {
        width: 100%;
        padding: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-image: url('/img/background/fetch-mobile.svg');
        background-repeat: no-repeat;
        background-position: bottom;
        background-size: cover;
        @media screen and (min-width: $above-tablet-length) {
            background-image: url('/img/background/fetch-desktop.svg');
        }
    }


    &__bloc {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: $white;
        border-radius: 12px;
        padding: 24px;
        @media screen and (min-width: $above-tablet-length) {
            max-width: 480px;
            padding: 32px;
        }
    }

    &__subtitle {
        font-size: 17px;
        line-height: 1.29;
        color: $moon;
        margin: 8px 0 16px;
    }

    &__input {
        margin-bottom: 3px;
    }

    &__cta {
        min-width: 160px;
        height: 56px;
        padding: 0 24px;
        border-radius: 100px;
        width: fit-content;
        max-width: 220px;
        background-color: $silver;
        color: $stone;
        font-size: 17px;
        font-weight: bold;
        cursor: not-allowed;
    }

    &__cta-active {
        background-color: $blue;
        color: $white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__cta-pass {
        margin-top: 8px;
        height: 40px;
        font-size: 16px;
        font-weight: bold;
        color: $stone;
        background-color: $white;
        cursor: pointer;
    }

    &__success-icon {
        width: 40px;
        height: 40px;
        margin-bottom: 16px;
    }
}

</style>
